import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Megaphone, Loader2, Download, Plus, Trash2, MousePointerClick,
  MailOpen, Users as UsersIcon, Reply, UserCheck, Info, Upload, X, Check,
} from "lucide-react";
import { formatApiError, useAuth } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full p-2.5 rounded-xl bg-soft border border-transparent focus:border-mint focus:outline-none text-sm text-forest";

const STATUS_COLOURS = {
  Sent: "bg-soft text-forest",
  Opened: "bg-blue-100 text-blue-800",
  Clicked: "bg-mint/20 text-forest",
  Replied: "bg-yellow-100 text-yellow-800",
  Lead: "bg-lime text-forest",
  Unsubscribed: "bg-red-100 text-red-800",
  Bounced: "bg-red-100 text-red-800",
};

// Google Sheet headers -> contact fields (case-insensitive, punctuation ignored)
const HEADER_MAP = {
  firstname: "firstName",
  lastname: "lastName",
  mobile: "phone",
  phonenumber: "phone",
  phone: "phone",
  emailaddress: "email",
  email: "email",
  companyname: "businessName",
  businessname: "businessName",
  company: "businessName",
  business: "businessName",
  state: "state",
  category: "category",
  trackedlink: "trackedLink",
};

// First email-looking token anywhere in a cell (handles "a@x.com; b@y.com")
const EMAIL_FIND_RE = /[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}/;
const EMAIL_STRIP_RE = /[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}/g;

function parseCsv(text) {
  const rows = [];
  let row = [], cur = "", inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQ) {
      if (ch === '"') {
        if (text[i + 1] === '"') { cur += '"'; i++; } else inQ = false;
      } else cur += ch;
    } else if (ch === '"') inQ = true;
    else if (ch === ",") { row.push(cur); cur = ""; }
    else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i++;
      row.push(cur); rows.push(row); row = []; cur = "";
    } else cur += ch;
  }
  if (cur !== "" || row.length) { row.push(cur); rows.push(row); }
  return rows.filter((r) => r.some((c) => c.trim() !== ""));
}

function csvToContacts(text) {
  const rows = parseCsv(text);
  if (rows.length < 2) return { error: "The CSV needs a header row plus at least one data row." };
  const header = rows[0].map((h) => HEADER_MAP[h.toLowerCase().replace(/[^a-z]/g, "")] || null);
  if (!header.includes("email")) {
    return { error: "Couldn't find an Email Address column. Expected headers like: First Name, Last Name, Mobile, Email Address, Company Name, State, Category." };
  }
  const contacts = [];
  let invalid = 0;
  for (const r of rows.slice(1)) {
    const c = { firstName: "", lastName: "", businessName: "", email: "", phone: "", state: "", category: "", token: "" };
    header.forEach((field, i) => {
      const val = (r[i] || "").trim();
      if (field === "trackedLink") {
        // Re-importing an exported merge CSV: preserve the tracking token
        const tm = val.match(/\/api\/t\/c\/([^\s"\/]+)/);
        if (tm) c.token = tm[1];
        return;
      }
      if (field && val && !c[field]) c[field] = val;
    });
    const m = c.email.match(EMAIL_FIND_RE);
    if (m) {
      c.email = m[0];
      contacts.push(c);
    } else {
      invalid++;
    }
  }
  return { contacts, invalid };
}

export default function OutreachPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [summary, setSummary] = useState(null);
  const [contacts, setContacts] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [paste, setPaste] = useState("");
  const [adding, setAdding] = useState(false);
  const [addResult, setAddResult] = useState("");
  const [exporting, setExporting] = useState(false);
  const [importRows, setImportRows] = useState(null); // {contacts, invalid, fileName}
  const fileRef = useRef(null);

  const load = useCallback(async (query = "") => {
    try {
      setError("");
      const [s, c] = await Promise.all([
        axios.get(`${API}/admin/outreach/summary`),
        axios.get(`${API}/admin/outreach/contacts`, { params: query ? { q: query } : {} }),
      ]);
      setSummary(s.data);
      setContacts(c.data.contacts);
      setStatuses(c.data.statuses);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const submitContacts = async (contacts, source) => {
    setAdding(true);
    setAddResult("");
    setError("");
    try {
      let added = 0, skipped = 0, invalid = 0;
      for (let i = 0; i < contacts.length; i += 500) {
        const r = await axios.post(`${API}/admin/outreach/contacts`, {
          campaign: "installer-growth-pilot",
          contacts: contacts.slice(i, i + 500),
        });
        added += r.data.added;
        skipped += r.data.skipped;
        invalid += r.data.invalid || 0;
      }
      setAddResult(`Imported ${added} recipient${added === 1 ? "" : "s"} from ${source}, skipped ${skipped} duplicate${skipped === 1 ? "" : "s"}${invalid ? `, ${invalid} invalid email${invalid === 1 ? "" : "s"} skipped` : ""}.`);
      setPaste("");
      setImportRows(null);
      if (fileRef.current) fileRef.current.value = "";
      await load(q.trim());
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const addFromPaste = (e) => {
    e.preventDefault();
    const contacts = [];
    let bad = 0;
    for (const line of paste.split("\n")) {
      if (!line.trim()) continue;
      const m = line.match(EMAIL_FIND_RE);
      if (!m) { bad++; continue; }
      const rest = line.replace(EMAIL_STRIP_RE, "").split(/[,\t;]/).map((p) => p.trim()).filter(Boolean);
      contacts.push({
        email: m[0],
        firstName: rest[0] || "",
        businessName: rest.slice(1).join(" "),
      });
    }
    if (contacts.length === 0) {
      setError("No valid lines found. Use one per line: email, first name, business name");
      return;
    }
    submitContacts(contacts, "paste" + (bad ? ` (${bad} invalid line${bad === 1 ? "" : "s"} ignored)` : ""));
  };

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError("");
    const reader = new FileReader();
    reader.onload = () => {
      const result = csvToContacts(String(reader.result || ""));
      if (result.error) {
        setImportRows(null);
        setError(result.error);
      } else if (result.contacts.length === 0) {
        setImportRows(null);
        setError("No rows with a valid email address found in that CSV.");
      } else {
        setImportRows({ ...result, fileName: file.name });
      }
    };
    reader.readAsText(file);
  };

  const setStatus = async (c, status) => {
    try {
      await axios.patch(`${API}/admin/outreach/contacts/${c.id}`, { status });
      await load(q.trim());
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const remove = async (c) => {
    try {
      await axios.delete(`${API}/admin/outreach/contacts/${c.id}`);
      await load(q.trim());
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const r = await axios.get(`${API}/admin/outreach/export.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "outreach-merge-installer-growth-pilot.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setExporting(false);
    }
  };

  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
    } catch { return iso; }
  };

  const categories = [...new Set((contacts || []).map((c) => c.category).filter(Boolean))].sort();
  const visible = (contacts || []).filter((c) => !categoryFilter || c.category === categoryFilter);

  return (
    <div data-testid="admin-outreach" className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-mint">CRM</p>
          <h1 className="mt-1 text-3xl md:text-4xl font-extrabold text-forest">Outreach</h1>
          <p className="mt-2 text-mid-neutral">
            Installer Growth Pilot cold-email campaign — per-person open and click tracking.
          </p>
        </div>
        {isAdmin && (
          <button
            type="button"
            onClick={exportCsv}
            disabled={exporting || !summary?.total}
            data-testid="outreach-export"
            className="inline-flex items-center gap-2 rounded-lg bg-forest text-white px-4 py-2.5 text-sm font-bold disabled:opacity-50"
          >
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Export merge CSV
          </button>
        )}
      </header>

      {/* How it works — admin only (trade-secret workflow) */}
      {isAdmin && (
        <div data-testid="outreach-howto" className="rounded-2xl border border-mint/40 bg-mint/5 p-5 flex gap-3">
          <Info className="h-5 w-5 text-mint shrink-0 mt-0.5" />
          <div className="text-sm text-forest space-y-1">
            <p className="font-bold">How to use with Google Sheets + Pabbly</p>
            <ol className="list-decimal ml-4 space-y-0.5 text-mid-neutral">
              <li>Upload your Google Sheet as a CSV below (File → Download → CSV) — columns are matched automatically.</li>
              <li>Click <strong>Export merge CSV</strong> and import it back into your sheet — it keeps all your columns and adds <code className="text-forest">trackedLink</code> + <code className="text-forest">trackingPixel</code>.</li>
              <li>In Pabbly, use <code className="text-forest">{"{{trackedLink}}"}</code> as the URL behind "View the Installer Growth Pilot", and place <code className="text-forest">{"{{trackingPixel}}"}</code> at the bottom of the HTML email body.</li>
              <li>Opens and clicks appear here automatically. If a recipient enquires or books, they're matched to a CRM lead.</li>
            </ol>
            <p className="text-xs text-mid-neutral pt-1">
              Note: tracked links resolve at <strong className="text-forest">greencirclegroup.com.au</strong> — they go live once the site is deployed. Some mail apps (e.g. Apple Mail) auto-load images, which can count as an "open".
            </p>
          </div>
        </div>
      )}

      {error && (
        <div data-testid="outreach-error" className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}

      {/* Summary */}
      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
          <StatCard label="Recipients" value={summary.total} Icon={UsersIcon} testId="outreach-stat-total" />
          <StatCard label={`Opened (${summary.openRate}%)`} value={summary.opened} Icon={MailOpen} testId="outreach-stat-opened" />
          <StatCard label={`Clicked (${summary.clickRate}%)`} value={summary.clicked} Icon={MousePointerClick} testId="outreach-stat-clicked" />
          <StatCard label="Replied" value={summary.replied} Icon={Reply} testId="outreach-stat-replied" />
          <StatCard label="Leads" value={summary.leads} Icon={UserCheck} testId="outreach-stat-leads" accent />
        </div>
      )}

      {/* Add recipients — admin only */}
      {isAdmin && (
      <section className="rounded-2xl bg-white border border-border-neutral p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 className="flex items-center gap-2 text-lg font-extrabold text-forest">
            <Plus className="h-4 w-4 text-mint" /> Add recipients
          </h2>
          <div>
            <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="outreach-csv-input" />
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              data-testid="outreach-csv-upload"
              className="inline-flex items-center gap-2 rounded-lg bg-forest text-white px-4 py-2 text-sm font-bold"
            >
              <Upload className="h-4 w-4" /> Upload CSV
            </button>
          </div>
        </div>

        {/* CSV import preview */}
        {importRows && (
          <div data-testid="outreach-import-preview" className="mt-4 rounded-xl border border-mint/40 bg-mint/5 p-4 space-y-3">
            <p className="text-sm font-bold text-forest">
              {importRows.fileName}: {importRows.contacts.length} valid recipient{importRows.contacts.length === 1 ? "" : "s"}
              {importRows.invalid > 0 && ` · ${importRows.invalid} row${importRows.invalid === 1 ? "" : "s"} skipped (no valid email)`}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left font-bold uppercase tracking-wider text-mid-neutral">
                    <th className="pb-1 pr-3">Name</th>
                    <th className="pb-1 pr-3">Email</th>
                    <th className="pb-1 pr-3">Company</th>
                    <th className="pb-1 pr-3">State</th>
                    <th className="pb-1">Category</th>
                  </tr>
                </thead>
                <tbody>
                  {importRows.contacts.slice(0, 8).map((c, i) => (
                    <tr key={i} className="border-t border-mint/20">
                      <td className="py-1.5 pr-3 text-forest">{c.firstName} {c.lastName}</td>
                      <td className="py-1.5 pr-3 text-forest">{c.email}</td>
                      <td className="py-1.5 pr-3 text-forest">{c.businessName || "—"}</td>
                      <td className="py-1.5 pr-3 text-forest">{c.state || "—"}</td>
                      <td className="py-1.5 text-forest">{c.category || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {importRows.contacts.length > 8 && (
                <p className="mt-1 text-[11px] text-mid-neutral">…and {importRows.contacts.length - 8} more</p>
              )}
            </div>
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => { setImportRows(null); if (fileRef.current) fileRef.current.value = ""; }}
                data-testid="outreach-import-cancel"
                className="inline-flex items-center gap-1.5 rounded-lg bg-soft text-forest px-3 py-2 text-xs font-bold">
                <X className="h-3.5 w-3.5" /> Cancel
              </button>
              <button type="button" onClick={() => submitContacts(importRows.contacts, "CSV")}
                disabled={adding}
                data-testid="outreach-import-confirm"
                className="inline-flex items-center gap-2 rounded-lg bg-forest text-white px-4 py-2 text-sm font-bold disabled:opacity-50">
                {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                Import {importRows.contacts.length} recipients
              </button>
            </div>
          </div>
        )}

        {/* Paste fallback */}
        <form onSubmit={addFromPaste} className="mt-4 space-y-3">
          <textarea
            rows={3}
            value={paste}
            onChange={(e) => setPaste(e.target.value)}
            placeholder={"Or paste one per line: email, first name, business name\ne.g. dave@solartech.com.au, Dave, Solar Tech Installations"}
            data-testid="outreach-paste"
            className={inputCls}
          />
          <div className="flex items-center justify-between gap-3">
            {addResult && <p data-testid="outreach-add-result" className="text-xs font-semibold text-forest">{addResult}</p>}
            <button
              type="submit"
              disabled={!paste.trim() || adding}
              data-testid="outreach-add-submit"
              className="ml-auto inline-flex items-center gap-2 rounded-lg bg-forest text-white px-4 py-2 text-sm font-bold disabled:opacity-50"
            >
              {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
              Add pasted recipients
            </button>
          </div>
        </form>
      </section>
      )}

      {/* Recipients */}
      <section className="rounded-2xl bg-white border border-border-neutral overflow-hidden">
        <div className="p-4 border-b border-border-neutral flex flex-wrap items-center gap-3">
          <h2 className="flex items-center gap-2 text-lg font-extrabold text-forest flex-1">
            <Megaphone className="h-4 w-4 text-mint" /> Recipients
            {contacts && <span className="text-xs font-semibold text-mid-neutral">({visible.length}{categoryFilter ? ` of ${contacts.length}` : ""})</span>}
          </h2>
          {categories.length > 0 && (
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              data-testid="outreach-category-filter"
              className="p-2 rounded-lg bg-soft border border-transparent focus:border-mint focus:outline-none text-xs font-semibold text-forest"
            >
              <option value="">All categories</option>
              {categories.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          )}
          <form onSubmit={(e) => { e.preventDefault(); load(q.trim()); }} className="flex items-center gap-2">
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              data-testid="outreach-search"
              className="p-2 rounded-lg bg-soft border border-transparent focus:border-mint focus:outline-none text-xs text-forest w-44"
            />
          </form>
        </div>

        {!contacts ? (
          <div className="flex items-center justify-center h-32 text-mid-neutral">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
          </div>
        ) : visible.length === 0 ? (
          <p className="p-6 text-sm text-mid-neutral text-center">{isAdmin ? "No recipients yet — upload your CSV above." : "No recipients yet."}</p>
        ) : (
          visible.map((c) => (
            <div key={c.id} data-testid={`outreach-row-${c.id}`}
              className="flex flex-wrap items-center gap-3 p-4 border-b border-border-neutral last:border-0">
              <div className="flex-1 min-w-0">
                <p className="font-bold text-forest truncate">
                  {c.firstName} {c.lastName}
                  {c.businessName && <span className="text-mid-neutral font-normal"> · {c.businessName}</span>}
                </p>
                <p className="text-xs text-mid-neutral truncate">
                  {c.email}
                  {c.state && ` · ${c.state}`}
                  {c.opens > 0 && ` · opened ${c.opens}× (first ${fmt(c.firstOpenedAt)})`}
                  {c.clicks > 0 && ` · clicked ${c.clicks}× (first ${fmt(c.firstClickedAt)})`}
                </p>
              </div>

              {c.category && (
                <span data-testid={`outreach-category-${c.id}`} className="rounded-full bg-soft text-forest px-2.5 py-1 text-[11px] font-bold">
                  {c.category}
                </span>
              )}

              {c.leadId && (
                <Link to={`/admin/leads/${c.leadId}`} data-testid={`outreach-lead-${c.id}`}
                  className="inline-flex items-center gap-1 rounded-lg bg-lime text-forest px-2.5 py-1.5 text-xs font-bold">
                  <UserCheck className="h-3 w-3" /> Lead
                </Link>
              )}

              <select
                value={c.status}
                onChange={(e) => setStatus(c, e.target.value)}
                data-testid={`outreach-status-${c.id}`}
                className={"rounded-full px-2.5 py-1 text-xs font-bold border-0 focus:outline-none " + (STATUS_COLOURS[c.status] || "bg-soft text-forest")}
              >
                {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>

              <button type="button" onClick={() => remove(c)} data-testid={`outreach-delete-${c.id}`}
                className="inline-flex items-center gap-1 rounded-lg bg-red-50 text-red-700 px-2.5 py-1.5 text-xs font-bold">
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          ))
        )}
      </section>
    </div>
  );
}

function StatCard({ label, value, Icon, accent = false, testId }) {
  return (
    <div data-testid={testId}
      className={"rounded-2xl p-5 " + (accent ? "bg-forest text-white" : "bg-white border border-border-neutral text-forest")}>
      <div className="flex items-center justify-between">
        <p className={"text-xs font-bold uppercase tracking-[0.15em] " + (accent ? "text-lime" : "text-mid-neutral")}>{label}</p>
        <Icon className={"h-4 w-4 " + (accent ? "text-lime" : "text-mint")} />
      </div>
      <p className="mt-3 text-3xl font-extrabold">{value}</p>
    </div>
  );
}
